import { PublicPageShell, PublicPageCta } from "./PublicPageShell";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

/**
 * RentPage — PUBLIC informational preview of the PLANNED "Parcel RENT" feature
 * (`#rent`). Informational only: no booking, payment, verification or rental
 * action exists here. All copy comes from the i18n layer (English canonical).
 */
export function RentPage() {
  const { dict, t } = useI18n();
  return (
    <PublicPageShell
      testId="rent-page"
      index="R · 01"
      eyebrow={t("rent.eyebrow")}
      title={
        <>
          {dict.rent.titleLine1}
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg,#FF4757 0%,#FF7A85 55%,#E0303F 100%)" }}
          >
            {dict.rent.titleHighlight}
          </span>
        </>
      }
      intro={t("rent.intro")}
      footer={<PublicPageCta />}
    >
      <RentStatusRow />
      <RentCoexistence />
      <RentSteps />
      <RentBusinessModel />
      <p
        data-testid="rent-disclaimer"
        className="mt-14 neon-glass p-5 md:p-6 border border-border-coral font-display text-[13px] leading-[1.65] text-text-secondary"
      >
        {t("rent.disclaimer")}
      </p>
    </PublicPageShell>
  );
}

function RentStatusRow() {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span
        data-testid="rent-release-badge"
        className="inline-flex items-center gap-2 h-8 px-3 rounded-sm border border-border-coral font-mono text-[11px] font-semibold tracking-[0.2em] uppercase text-neon-coral"
      >
        <span aria-hidden className="grid-rent-dot h-1.5 w-1.5 rounded-full bg-neon-coral" />
        {t("rent.release")}
      </span>
      <span
        data-testid="rent-status-badge"
        className="inline-flex items-center min-h-8 py-1.5 px-3 rounded-sm border border-border-soft font-mono text-[10.5px] tracking-[0.2em] uppercase text-text-muted"
      >
        {t("rent.status")}
      </span>
    </div>
  );
}

const COLS = 16;
const ROWS = 10;
// Illustrative sponsor rectangle inside one owner territory (cols 10–14, rows 2–6).
const isSponsorCell = (c: number, r: number) => c >= 10 && c <= 14 && r >= 2 && r <= 6;

function RentCoexistence() {
  const { dict } = useI18n();
  const c = dict.rent.coexist;
  return (
    <section data-testid="rent-coexistence" className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
      <div className="lg:col-span-5">
        <SectionEyebrow text={c.eyebrow} />
        <h2 className="mt-4 font-hero uppercase text-[26px] md:text-[32px] leading-tight text-text-primary">{c.title}</h2>
        <p className="mt-4 font-display text-[14px] leading-[1.65] text-text-secondary">{c.body}</p>
      </div>
      <figure className="lg:col-span-7 neon-glass p-5 md:p-6 m-0">
        <TerritoryDiagram ownerLabel={c.ownerLabel} sponsorLabel={c.sponsorLabel} />
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 font-display text-[12.5px] text-text-secondary">
          <span data-testid="rent-legend-owner" className="inline-flex items-center gap-2">
            <i aria-hidden className="h-2.5 w-2.5 rounded-[2px] bg-[rgba(45,182,240,0.55)]" />
            {c.ownerLegend}
          </span>
          <span data-testid="rent-legend-sponsor" className="inline-flex items-center gap-2">
            <i aria-hidden className="h-2.5 w-2.5 rounded-[2px] bg-[rgba(255,71,87,0.75)]" />
            {c.sponsorLegend}
          </span>
        </div>
        <figcaption className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-text-muted">{c.caption}</figcaption>
      </figure>
    </section>
  );
}

function TerritoryDiagram({ ownerLabel, sponsorLabel }: { ownerLabel: string; sponsorLabel: string }) {
  const cells = Array.from({ length: COLS * ROWS }, (_, i) => isSponsorCell(i % COLS, Math.floor(i / COLS)));
  return (
    <div data-testid="rent-territory-diagram" className="relative" role="img" aria-label={`${ownerLabel} · ${sponsorLabel}`}>
      <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}>
        {cells.map((sponsor, i) => (
          <span
            key={i}
            aria-hidden
            className={cn(
              "aspect-square rounded-[2px] border",
              sponsor
                ? "bg-[rgba(255,71,87,0.24)] border-[rgba(255,71,87,0.7)] shadow-glow-coral-sm"
                : "bg-[rgba(45,182,240,0.10)] border-[rgba(45,182,240,0.32)]",
            )}
          />
        ))}
      </div>
      <DiagramTag testid="rent-diagram-owner-tag" text={ownerLabel} className="left-[6%] top-[58%] text-neon-teal border-border-accent" />
      <DiagramTag testid="rent-diagram-sponsor-tag" text={sponsorLabel} className="left-[64%] top-[38%] text-neon-coral border-border-coral" />
    </div>
  );
}

function DiagramTag({ testid, text, className }: { testid: string; text: string; className: string }) {
  return (
    <span
      data-testid={testid}
      className={cn(
        "absolute px-2 py-1 rounded-sm border bg-obsidian/80 backdrop-blur-sm",
        "font-mono text-[10px] font-semibold tracking-[0.2em] uppercase",
        className,
      )}
    >
      {text}
    </span>
  );
}

function SectionEyebrow({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden className="block h-px w-8 bg-text-muted" />
      <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-text-secondary">{text}</span>
    </div>
  );
}

function RentSteps() {
  const { dict } = useI18n();
  return (
    <section data-testid="rent-steps" className="mt-16">
      <SectionEyebrow text={dict.rent.how.eyebrow} />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {dict.rent.how.steps.map((s, i) => (
          <article key={s.title} data-testid={`rent-step-${i + 1}`} className="border-t border-border-strong pt-4">
            <span className="font-mono text-[11px] font-semibold tracking-[0.24em] text-neon-coral">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-2 font-display text-[15px] font-semibold text-text-primary">{s.title}</h3>
            <p className="mt-1.5 font-display text-[13px] leading-[1.6] text-text-secondary">{s.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function RentBusinessModel() {
  const { dict } = useI18n();
  const m = dict.rent.model;
  return (
    <section data-testid="rent-business-model" className="mt-16">
      <SectionEyebrow text={m.eyebrow} />
      <h2 className="mt-4 max-w-[820px] font-hero uppercase text-[24px] md:text-[30px] leading-tight text-text-primary">{m.title}</h2>
      <p className="mt-4 max-w-[680px] font-display text-[14px] leading-[1.65] text-text-secondary">{m.body}</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {m.cards.map((card, i) => (
          <article
            key={card.title}
            data-testid={`rent-model-card-${i + 1}`}
            className="neon-glass p-6 flex flex-col gap-2 transition-transform duration-med hover:-translate-y-0.5"
          >
            <span className="font-mono text-[10.5px] font-semibold tracking-[0.24em] uppercase text-neon-teal">{card.title}</span>
            <p className="font-display text-[13.5px] leading-[1.6] text-text-secondary">{card.body}</p>
          </article>
        ))}
      </div>
      <p data-testid="rent-model-note" className="mt-4 font-mono text-[10.5px] tracking-[0.14em] uppercase text-text-muted">
        {m.note}
      </p>
    </section>
  );
}
